/**
 * TeamPitchRows (EPIC-110 D2/D6): one horizontal row per team (home, then
 * away), each holding the team zone summary strip on top and then, left to
 * right, the team heat map, the team zone grid (`ControlCard`) and the team
 * thirds pitch. Three columns from `lg`, stacked below. Used by
 * `PeriodMapsSection` (per period) and `HeatMapSection` (Generales).
 *
 * The whole thing mounts under ONE `TeamControlMap` root (title, caption and
 * root testid unchanged) via its `renderCards` hook.
 */
import { useMemo, type FC } from "react";
import SoccerFieldHeatMap from "./SoccerFieldHeatMap";
import TeamControlMap from "./TeamControlMap";
import TeamThirdsPitch from "./TeamThirdsPitch";
import TeamZoneSummaryStrip from "./TeamZoneSummaryStrip";
import { ROW_LABEL_CLASS } from "./pitchRowLayout";
import { resolveMatchTeamColors } from "./resolveMatchTeamColors";
import {
  computeHeatMapData,
  extractHeatPoints,
} from "../../utils/heatMapZones";
import type { TeamSide } from "../../utils/teamControlMap";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import type { Match } from "../../types";

interface TeamPitchRowsProps {
  match: Match;
  events: MatchEvent[];
  t: (key: string, fallback?: string) => string;
  rotated?: boolean;
  captionOverride?: string;
  /** Root testid of the (single) TeamControlMap. */
  controlTestId: string;
  /** Per-side testids for the heat map, thirds pitch, strip and row. */
  ids: Record<
    TeamSide,
    { heat: string; thirds: string; strip: string; row: string }
  >;
  /** Mark everything except the heat maps `data-export-hidden` (Generales). */
  exportHiddenNonHeat?: boolean;
}

const ACCENT: Record<TeamSide, string> = {
  home: "#10b981",
  away: "#6366f1",
};

/** Equal-width column: same box, border and padding for all three pitches. */
const COLUMN_CLASS =
  "mx-auto flex w-full min-w-0 max-w-md flex-col gap-2 rounded-lg border border-slate-700 bg-slate-900/40 p-2";

const TeamPitchRows: FC<TeamPitchRowsProps> = ({
  match,
  events,
  t,
  rotated = false,
  captionOverride,
  controlTestId,
  ids,
  exportHiddenNonHeat = false,
}) => {
  const homeId = match.home_team.id;
  const awayId = match.away_team.id;
  const heat = useMemo(
    () => ({
      home: {
        data: computeHeatMapData(events, homeId),
        points: extractHeatPoints(events, homeId),
      },
      away: {
        data: computeHeatMapData(events, awayId),
        points: extractHeatPoints(events, awayId),
      },
    }),
    [events, homeId, awayId],
  );
  const kit = useMemo(
    () => resolveMatchTeamColors(match),
    [match.home_team, match.away_team],
  );
  const hiddenAttr = exportHiddenNonHeat ? "true" : undefined;

  return (
    <TeamControlMap
      match={match}
      events={events}
      t={t}
      rotated={rotated}
      captionOverride={captionOverride}
      exportHiddenHeader={exportHiddenNonHeat}
      titleKey="analytics.heatControlMapTitle"
      data-testid={controlTestId}
      renderCards={({ cards, colors }) => (
        <div className="flex flex-col gap-6">
          {(["home", "away"] as TeamSide[]).map((side) => {
            const team = side === "home" ? match.home_team : match.away_team;
            return (
              <div
                key={side}
                className="flex flex-col gap-3"
                data-testid={ids[side].row}
              >
                <div data-export-hidden={hiddenAttr}>
                  {
                    <TeamZoneSummaryStrip
                      team={team}
                      side={side}
                      events={events}
                      color={kit[side]}
                      t={t}
                      data-testid={ids[side].strip}
                    />
                  }
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3 md:gap-2 lg:gap-3">
                  <div className={COLUMN_CLASS}>
                    <div
                      className={ROW_LABEL_CLASS}
                      data-testid={`${ids[side].row}-pitch-label-heat`}
                    >
                      {t("analytics.pitchLabelHeat", "Heat map")}
                    </div>
                    <SoccerFieldHeatMap
                      data={heat[side].data}
                      points={heat[side].points}
                      title={team.short_name || team.name}
                      accentColor={ACCENT[side]}
                      team={team}
                      side={side}
                      rotated={rotated}
                      pitchArrows
                      rowLayout
                      t={t}
                      data-testid={ids[side].heat}
                    />
                  </div>
                  <div className={COLUMN_CLASS} data-export-hidden={hiddenAttr}>
                    <div
                      className={ROW_LABEL_CLASS}
                      data-testid={`${ids[side].row}-pitch-label-grid`}
                    >
                      {t("analytics.pitchLabelGrid", "Zone control")}
                    </div>
                    {cards[side]}
                  </div>
                  <div className={COLUMN_CLASS} data-export-hidden={hiddenAttr}>
                    <div
                      className={ROW_LABEL_CLASS}
                      data-testid={`${ids[side].row}-pitch-label-thirds`}
                    >
                      {t("analytics.thirdsPitchTitle", "Passes by third")}
                    </div>
                    <TeamThirdsPitch
                      team={team}
                      side={side}
                      events={events}
                      color={colors[side]}
                      t={t}
                      rotated={rotated}
                      rowLayout
                      data-testid={ids[side].thirds}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    />
  );
};

export default TeamPitchRows;
